import { Box, Button, Container, Grid, Typography, useMediaQuery } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useGlobalSnackbar } from "../../../context/SnackbarContext";
import { RootState } from "../../../redux/store";
import { colors } from "../../ui/colors";
import { useDialog } from "../../ui/dialogs/useDialog";
import { filledButtonSecondary } from "../../ui/styles/buttonStyles";

// Sub-components
import { ClientInfoCard } from "./ClientInfo";
import { EmailRecipient, EmailRecipientsList, useEmailRecipients } from "./EmailRecipients";
import { NotesAndTermsInput, useNotesAndTerms } from "./NotesAndTerms";
import { PaymentMethodSelector, usePaymentMethodState, useSetPaymentMethod } from "./PaymentMethods";
import { InvoiceExtraLineItemsCard, type InvoiceExtraLineItem } from "./components/InvoiceExtraLineItemsCard";
import { InvoiceMultiJobTotalsCard } from "./components/InvoiceMultiJobTotalsCard";
import { InvoiceServiceFeesCard, type InvoiceServiceFeeDraft } from "./components/InvoiceServiceFeesCard";
import { CC_FEE_ID, PaymentProcessingFeeCard } from "./components/PaymentProcessingFeeCard";

// Hooks / Actions
import { useCreateSystemInvoice } from "./hooks/useCreateSystemInvoice";
import { useCompanyPaymentsStatusQuery } from "./hooks/usePayments";

// Types / Mappers
import { extraLineItemApplyToKey, resolveApplyToBaseAmount, sanitizeAmountDraft } from "./shared/utils";
import { GenericInvoiceInput } from "./types/genericInvoice";
import { mapGenericInvoiceToBackendPayload } from "./utils/mapGenericInvoiceData";

interface InvoiceCreateAdapterProps {
  initialData: GenericInvoiceInput;
  invoiceModal?: string;
  phoneNumber?: string;
}

export const InvoiceCreateAdapter = ({ initialData, invoiceModal, phoneNumber }: InvoiceCreateAdapterProps) => {
  const [sendEmail, setSendEmail] = useState(initialData.sendEmail !== false);
  const [showValidation, setShowValidation] = useState(false);
  const { reset: resetPaymentMethod } = useSetPaymentMethod();

  const clientInfo = useMemo(
    () => ({
      firstName: initialData.client.name,
      lastName: "",
      alias: initialData.client.alias || null,
      email: initialData.client.email,
      phone: initialData.client.phone,
      avatar: initialData.client.avatar || "",
    }),
    [initialData.client],
  );

  const company = useSelector((state: RootState) => state.userState.company);
  useCompanyPaymentsStatusQuery(company?.id);

  const paymentMethod = usePaymentMethodState();

  useEffect(() => {
    resetPaymentMethod();
  }, [resetPaymentMethod]);

  const hasClientPaymentMethods = paymentMethod.selectedPayments.length > 0 && !paymentMethod.markAsPaid;

  const initialEmailRecipients = useMemo<EmailRecipient[]>(() => {
    if (initialData.client.email) {
      return [
        {
          id: "1",
          email: initialData.client.email,
          permissionLevel: "Can Pay",
          isLocked: true,
        },
      ];
    }
    return [];
  }, [initialData.client.email]);

  const { emailRecipients, addRecipient, deleteRecipient, updateRecipient, setAllPermissionLevels } = useEmailRecipients(
    initialEmailRecipients,
    "Can Pay",
  );

  const globalFeeLabel = (company as any)?.invoiceCustomStyle?.labels?.increasePercentageByFrequency;

  const { notes, updateNotes } = useNotesAndTerms(initialData.notes || "");

  const { createSystemInvoice, isPending } = useCreateSystemInvoice();
  const { showSnackbar } = useGlobalSnackbar();
  const { showDialog, DialogComponent } = useDialog();

  const [extraLineItems, setExtraLineItems] = useState<InvoiceExtraLineItem[]>(() =>
    (initialData.lineItems || []).map((item) => ({
      id: item.id || `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      title: item.title,
      amount: String(item.amount),
      description: item.description || "",
      isPassedItem: true,
      originalAmount: String(item.amount),
    })),
  );

  const normalizedExtraLineItems = useMemo(
    () =>
      extraLineItems
        .map((item) => ({
          id: item.id,
          title: item.title.trim(),
          amount: Math.round((Number.parseFloat(String(item.amount || "0")) || 0) * 100) / 100,
          description: item.description?.trim() || undefined,
        }))
        .filter((item) => item.title.length > 0 && item.amount >= 0),
    [extraLineItems],
  );

  const subtotal = useMemo(
    () => Math.round(normalizedExtraLineItems.reduce((acc, item) => acc + item.amount, 0) * 100) / 100,
    [normalizedExtraLineItems],
  );

  const [serviceFees, setServiceFees] = useState<InvoiceServiceFeeDraft[]>(() => {
    const initialValue = Number(initialData.increasePercentageByFrequency?.value || 0);
    if (!(initialValue > 0)) return [];
    return [
      {
        id: "initial-service-fee",
        label: initialData.increasePercentageByFrequency?.label || globalFeeLabel || "Service fee",
        kind: "amount",
        value: String(initialValue),
        appliesToType: "invoice",
        appliesToKey: "invoice",
      },
    ];
  });

  const serviceFeeApplyToOptions = useMemo(
    () => [
      { key: "invoice", label: "Entire invoice", type: "invoice" as const },
      ...extraLineItems.map((item, index) => ({
        key: extraLineItemApplyToKey(item.id),
        label: item.title.trim() || `Line item ${index + 1}`,
        type: "extra_line_item" as const,
        extraLineItemId: item.id,
      })),
    ],
    [extraLineItems],
  );

  const applyToAmountByKey = useMemo(() => {
    const map: Record<string, number> = {};
    extraLineItems.forEach((item) => {
      map[extraLineItemApplyToKey(item.id)] = Math.round((Number.parseFloat(String(item.amount || "0")) || 0) * 100) / 100;
    });
    return map;
  }, [extraLineItems]);

  const serviceFeeComputedAmounts = useMemo(() => {
    const map: Record<string, number> = {};
    serviceFees.forEach((item) => {
      const value = Number.parseFloat(String(item.value || "0")) || 0;
      const base = resolveApplyToBaseAmount(item.appliesToType, item.appliesToKey, subtotal, applyToAmountByKey);
      map[item.id] =
        item.kind === "percentage"
          ? Math.round(((Math.max(0, base) * Math.min(Math.max(0, value), 100)) / 100) * 100) / 100
          : Math.round(Math.max(0, value) * 100) / 100;
    });
    return map;
  }, [serviceFees, subtotal, applyToAmountByKey]);

  const serviceFeeAmount = useMemo(
    () => Math.round(Object.values(serviceFeeComputedAmounts).reduce((acc, amount) => acc + amount, 0) * 100) / 100,
    [serviceFeeComputedAmounts],
  );

  const normalizedServiceFees = useMemo(
    () =>
      serviceFees
        .map((item) => {
          const amount = Math.round((serviceFeeComputedAmounts[item.id] ?? 0) * 100) / 100;
          const label = item.label.trim();
          const value = Number.parseFloat(String(item.value || "0")) || 0;
          if (!label || !(amount > 0)) return null;
          const option = serviceFeeApplyToOptions.find((opt) => opt.key === (item.appliesToKey || "invoice"));
          return {
            alias: label,
            kind: item.kind,
            value,
            amount,
            appliesTo: option
              ? {
                  type: option.type,
                  ...(option.type === "extra_line_item" && option.extraLineItemId ? { extraLineItemId: option.extraLineItemId } : {}),
                  label: option.label,
                }
              : { type: "invoice" as const },
          };
        })
        .filter((item): item is NonNullable<typeof item> => item != null),
    [serviceFees, serviceFeeComputedAmounts, serviceFeeApplyToOptions],
  );

  // Grand total manual overrides
  const [manualGrandTotal, setManualGrandTotal] = useState<number | null>(null);

  const computedGrandTotal = useMemo(() => {
    return Math.round((subtotal + serviceFeeAmount) * 100) / 100;
  }, [subtotal, serviceFeeAmount]);

  const displayTotal = useMemo(() => {
    return manualGrandTotal !== null ? manualGrandTotal : computedGrandTotal;
  }, [manualGrandTotal, computedGrandTotal]);

  const isTotalOverridden = manualGrandTotal !== null;

  useEffect(() => {
    setManualGrandTotal(null);
  }, [extraLineItems, serviceFees]);

  const hasIncompleteExtraLineItem = useMemo(
    () =>
      extraLineItems.some((item) => {
        const title = item.title.trim();
        const amount = Number.parseFloat(String(item.amount || "0"));
        return title.length === 0 || !(Number.isFinite(amount) && amount >= 0);
      }),
    [extraLineItems],
  );

  const hasIncompleteServiceFee = useMemo(
    () =>
      serviceFees.some((item) => {
        const label = item.label.trim();
        const value = Number.parseFloat(String(item.value || "0"));
        return label.length === 0 || !(Number.isFinite(value) && value >= 0);
      }),
    [serviceFees],
  );

  const handleRemoveExtraLineItem = (id: string) => {
    const applyKey = extraLineItemApplyToKey(id);
    setExtraLineItems((prev) => prev.filter((item) => item.id !== id));
    setServiceFees((prev) => prev.filter((item) => item.appliesToKey !== applyKey));
  };

  const handleResetAmount = (id: string) => {
    setExtraLineItems((prev) => prev.map((item) => (item.id === id && item.isPassedItem ? { ...item, amount: item.originalAmount || "" } : item)));
  };

  const handleGenerateInvoice = () => {
    try {
      if (extraLineItems.length === 0) {
        showSnackbar("Please add at least one line item to include on the invoice.", "warning", 5000, "top-center");
        return;
      }

      if (hasIncompleteExtraLineItem) {
        setShowValidation(true);
        showSnackbar("Complete each line item with title and a valid amount before generating.", "warning", 5000, "top-center");
        return;
      }

      if (hasIncompleteServiceFee) {
        setShowValidation(true);
        showSnackbar("Complete each service fee with a label and a valid amount, or clear it.", "warning", 5000, "top-center");
        return;
      }

      if (sendEmail && (!emailRecipients || emailRecipients.length === 0)) {
        showSnackbar("Please add at least one email recipient or disable email delivery.", "warning", 5000, "top-center");
        return;
      }

      const hasClientPaymentMethods = paymentMethod.selectedPayments.length > 0 && !paymentMethod.markAsPaid;
      const hasCanPayRecipient = emailRecipients.some((recipient) => recipient.permissionLevel === "Can Pay");
      if (sendEmail && hasClientPaymentMethods && !hasCanPayRecipient) {
        showSnackbar("Select at least one recipient with Can Pay, or remove the payment methods.", "warning", 5000, "top-center");
        return;
      }

      const submitInvoice = () => {
        const invoicePayload = mapGenericInvoiceToBackendPayload({
          identifierId: initialData.identifierId,
          lineItems: normalizedExtraLineItems,
          paymentMethod,
          emailRecipients,
          notes,
          sendEmail,
          feeAmount: serviceFeeAmount,
          serviceFees: normalizedServiceFees,
          externalUid: initialData.externalUid,
          companyId: company?.id,
          phoneNumber,
        });
        createSystemInvoice(invoicePayload, { invoiceModal: invoiceModal || `Dashboard-GenericInvoiceCreate` });
      };

      if (!hasClientPaymentMethods) {
        showDialog({
          type: "confirm",
          title: "No payment method selected",
          message:
            "This invoice has no client payment method. Recipients will only be able to view it and will not receive a payment link. Do you want to continue?",
          confirmLabel: "Generate invoice",
          cancelLabel: "Cancel",
          onConfirm: submitInvoice,
          onCancel: () => {},
        });
        return;
      }

      submitInvoice();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "An error occurred while preparing invoice data.";
      showSnackbar(errorMessage, "error", 5000, "top-center");
    }
  };

  const isXs = useMediaQuery("(max-width:600px)");
  const hasCanPayRecipient = emailRecipients.some((recipient) => recipient.permissionLevel === "Can Pay");
  const needsPaymentMethodForCanPay = sendEmail && hasCanPayRecipient && !hasClientPaymentMethods;
  const isGenerateDisabled = isPending || hasIncompleteExtraLineItem || needsPaymentMethodForCanPay;

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 2, sm: 3 },
        pb: { xs: 5, sm: 6 },
        px: { xs: 2, sm: 3 },
        backgroundColor: colors.lightGray.base,
        position: "relative",
      }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key="generic-editor-view"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} lg={8}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: { xs: 1, sm: 2 } }}>
                  <Box>
                    <Typography
                      variant="h3"
                      fontWeight={900}
                      sx={{
                        letterSpacing: "-0.033em",
                        mb: 1,
                        fontSize: { xs: "1.75rem", sm: "2.5rem" },
                      }}
                    >
                      Create Invoice
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, color: "text.secondary", flexWrap: "wrap" }}>
                      <Typography variant="body1" sx={{ fontSize: { xs: "0.75rem", sm: "1rem" } }}>
                        {initialData.identifierId || "INV-GENERIC"}
                      </Typography>
                      <Box sx={{ width: { xs: 3, sm: 4 }, height: { xs: 3, sm: 4 }, bgcolor: "grey.400", borderRadius: "50%" }} />
                      <Typography variant="body1" sx={{ fontSize: { xs: "0.75rem", sm: "1rem" } }}>
                        Issued:{" "}
                        {new Date().toLocaleDateString("en-US", {
                          month: "short",
                          day: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                        })}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <ClientInfoCard client={clientInfo} isLoading={false} />

                <InvoiceExtraLineItemsCard
                  items={extraLineItems}
                  onAdd={() =>
                    setExtraLineItems((prev) => [
                      ...prev,
                      {
                        id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                        title: "",
                        amount: "",
                        description: "",
                      },
                    ])
                  }
                  onRemove={handleRemoveExtraLineItem}
                  onChange={(id, field, value) =>
                    setExtraLineItems((prev) =>
                      prev.map((item) => (item.id === id ? { ...item, [field]: field === "amount" ? sanitizeAmountDraft(value) : value } : item)),
                    )
                  }
                  totalAmount={subtotal}
                  showValidation={showValidation}
                  onResetAmount={handleResetAmount}
                />

                <InvoiceMultiJobTotalsCard
                  lineItemsSubtotal={subtotal}
                  serviceFeeAmount={serviceFeeAmount}
                  serviceFeeLabel="Service fees"
                  extraLineItemsAmount={0}
                  computedGrandTotal={computedGrandTotal}
                  displayTotal={displayTotal}
                  isTotalOverridden={isTotalOverridden}
                  onTotalChange={(v) => {
                    if (v == null) return;
                    const rounded = Math.round(v * 100) / 100;
                    if (computedGrandTotal != null && rounded === Math.round(computedGrandTotal * 100) / 100) {
                      setManualGrandTotal(null);
                    } else {
                      setManualGrandTotal(rounded);
                    }
                  }}
                  onTotalReset={() => setManualGrandTotal(null)}
                />

                <InvoiceServiceFeesCard
                  items={serviceFees}
                  applyToOptions={serviceFeeApplyToOptions}
                  computedAmounts={serviceFeeComputedAmounts}
                  onAdd={() =>
                    setServiceFees((prev) => [
                      ...prev,
                      {
                        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                        label: globalFeeLabel || "Service fee",
                        kind: "amount",
                        value: "",
                        appliesToType: "invoice",
                        appliesToKey: "invoice",
                      },
                    ])
                  }
                  onRemove={(id) => setServiceFees((prev) => prev.filter((item) => item.id !== id))}
                  onChange={(id, patch) => setServiceFees((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)))}
                  totalAmount={serviceFeeAmount}
                  showValidation={showValidation}
                />

                {/* Email Recipients */}
                <EmailRecipientsList
                  recipients={emailRecipients}
                  sendEmail={sendEmail}
                  needsPaymentMethod={needsPaymentMethodForCanPay}
                  allowCanPay={true}
                  onAdd={addRecipient}
                  onDelete={deleteRecipient}
                  onUpdate={updateRecipient}
                  onSendEmailChange={setSendEmail}
                />

                <NotesAndTermsInput value={notes} onChange={updateNotes} />
              </Box>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Box
                sx={{
                  position: { xs: "static", lg: "sticky" },
                  top: { lg: 24 },
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <PaymentMethodSelector availablePayments={{ paypal: true, stripe: true }} bypassCompanyStatus={true} />
                <PaymentProcessingFeeCard
                  serviceFees={serviceFees}
                  onServiceFeesChange={setServiceFees}
                  computedFeeAmount={(() => {
                    const ccFeeItem = serviceFees.find((f) => f.id === CC_FEE_ID || f.label.toLowerCase().trim() === "credit card fee");
                    return ccFeeItem ? (serviceFeeComputedAmounts[ccFeeItem.id] ?? 0) : 0;
                  })()}
                />
              </Box>
            </Grid>
          </Grid>
        </motion.div>
      </AnimatePresence>

      {/* Sticky Bottom */}
      <Box
        sx={{
          position: "sticky",
          bottom: { xs: 16, sm: 20 },
          display: "flex",
          justifyContent: "flex-end",
          pointerEvents: "none",
          zIndex: 10,
          mt: 2,
        }}
      >
        <Button
          sx={{
            ...filledButtonSecondary,
            pointerEvents: "auto",
            minWidth: 160,
            height: 48,
            px: 3.5,
            borderRadius: "999px",
            fontSize: 15,
            fontWeight: 700,
            boxShadow: "0 6px 20px rgba(251, 142, 0, 0.4)",
            "&:hover": {
              boxShadow: "0 8px 24px rgba(251, 142, 0, 0.5)",
            },
          }}
          onClick={handleGenerateInvoice}
          disabled={isGenerateDisabled}
        >
          {isPending ? "Creating..." : "Generate Invoice"}
        </Button>
      </Box>
      {DialogComponent}
    </Container>
  );
};
